import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useIsMobile } from '../components/Layout'
import RemindersPanel from '../components/RemindersPanel'
import { getInvoiceTotal } from '../lib/jobFinancials'

const SITE_VISIT_STATUS_COLOR = { Open: '#d97706', Converted: '#16a34a', Archived: '#6b7280' }

export function ClientsList() {
  const { isAdmin } = useAuth()
  const [clients, setClients] = useState([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchClients() }, [])

  async function fetchClients() {
    const { data } = await supabase.from('clients').select('*, jobs(id)').order('name')
    setClients(data || [])
    setLoading(false)
  }

  const filtered = clients.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase())
    const matchFilter = filter === 'all' || (filter === 'active' ? c.active !== false : c.active === false)
    return matchSearch && matchFilter
  })

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Clients</h1>
          <p style={styles.sub}>{clients.filter(c => c.active !== false).length} active · {clients.filter(c => c.active === false).length} inactive</p>
        </div>
        {isAdmin && <Link to="/clients/new" style={styles.addBtn}>+ Add Client</Link>}
      </div>

      <input
        style={styles.search}
        placeholder="🔍  Search by name, phone, or email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div style={styles.filters}>
        {['active', 'inactive', 'all'].map(f => (
          <button key={f} style={{ ...styles.filterBtn, ...(filter === f ? styles.filterActive : {}) }} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div style={styles.loading}>Loading...</div> : (
        <div style={styles.list}>
          {filtered.map(client => (
            <Link to={`/clients/${client.id}`} key={client.id} style={{ ...styles.card, opacity: client.active === false ? 0.6 : 1 }}>
              <div style={styles.avatar}>{client.name.charAt(0).toUpperCase()}</div>
              <div style={styles.info}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={styles.name}>{client.name}</span>
                  {client.active === false && <span style={styles.inactiveBadge}>Inactive</span>}
                </div>
                <span style={styles.detail}>{client.phone || client.email || 'No contact info'}</span>
                {client.address && <span style={styles.detail}>📍 {client.address}</span>}
              </div>
              <div style={styles.jobCount}>
                <span style={styles.jobNum}>{client.jobs?.length || 0}</span>
                <span style={styles.jobLabel}>jobs</span>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && <div style={styles.empty}>No clients found.</div>}
        </div>
      )}
    </div>
  )
}

export function ClientProfile() {
  const { isAdmin, profile } = useAuth()
  const isMobile = useIsMobile()
  const [client, setClient] = useState(null)
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()

  useEffect(() => { fetchClient() }, [id])

  const [estimates, setEstimates] = useState([])
  const [invoices, setInvoices] = useState([])
  const [siteVisits, setSiteVisits] = useState([])
  const [proposals, setProposals] = useState([])
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState('')

  async function fetchClient() {
    const [clientRes, jobsRes, estRes, invRes, visitsRes, propRes] = await Promise.all([
      supabase.from('clients').select('*').eq('id', id).single(),
      supabase.from('jobs').select('*').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('estimates').select('id, quote_number, suggested_price, status, created_at').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('invoices').select('id, invoice_number, amount, total, status, due_date').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('site_visits').select('id, title, status, created_at').eq('client_id', id).order('created_at', { ascending: false }),
      supabase.from('proposals').select('id, proposal_number, status, selected_contract_total, tax_amount, created_at').eq('client_id', id).eq('is_current_version', true).order('created_at', { ascending: false }),
    ])
    setClient(clientRes.data)
    setJobs(jobsRes.data || [])
    setEstimates(estRes.data || [])
    setInvoices(invRes.data || [])
    setSiteVisits(visitsRes.data || [])
    setProposals(propRes.data || [])
    setLoading(false)
  }

  function startEdit() {
    setEditForm({
      name: client.name || '', company: client.company || '', phone: client.phone || '',
      email: client.email || '', address: client.address || '', billing_address: client.billing_address || '',
      client_type: client.client_type || 'residential', notes: client.notes || '',
    })
    setEditError('')
    setEditing(true)
  }

  async function saveClientEdit() {
    if (!editForm.name.trim()) { setEditError('Client name is required.'); return }
    setSavingEdit(true); setEditError('')
    const payload = {
      name: editForm.name.trim(), company: editForm.company.trim() || null,
      phone: editForm.phone.trim() || null, email: editForm.email.trim() || null,
      address: editForm.address.trim() || null, billing_address: editForm.billing_address.trim() || null,
      client_type: editForm.client_type, notes: editForm.notes.trim() || null,
    }
    const { error } = await supabase.from('clients').update(payload).eq('id', id)
    if (error) { setEditError('Save failed: ' + error.message); setSavingEdit(false); return }
    setClient(c => ({ ...c, ...payload }))
    setSavingEdit(false)
    setEditing(false)
  }

  async function toggleActive() {
    setToggling(true)
    const newActive = client.active === false ? true : false
    await supabase.from('clients').update({ active: newActive }).eq('id', id)
    setClient(c => ({ ...c, active: newActive }))
    setToggling(false)
  }

  async function deleteClient() {
    if (!window.confirm(`Are you sure you want to permanently delete ${client.name}? This cannot be undone.`)) return
    setDeleting(true)
    const { error } = await supabase.from('clients').delete().eq('id', id)
    if (error) { alert('Failed to delete client: ' + error.message); setDeleting(false); return }
    navigate('/clients')
  }

  const [creatingVisit, setCreatingVisit] = useState(false)
  async function createSiteVisit() {
    setCreatingVisit(true)
    const { data, error } = await supabase.from('site_visits').insert([{ client_id: id, created_by: profile?.id }]).select().single()
    if (error) { alert('Failed to create site visit: ' + error.message); setCreatingVisit(false); return }
    navigate(`/site-visits/${data.id}`)
  }

  if (loading) return <div style={styles.loading}>Loading...</div>
  if (!client) return <div style={styles.loading}>Client not found.</div>

  const statusColor = { active: '#16a34a', completed: '#2563eb', pending: '#d97706', cancelled: '#dc2626' }
  const totalRevenue = jobs.reduce((sum, j) => sum + (j.contract_amount || 0), 0)
  const isActive = client.active !== false

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.back}>← Back</button>

      <div style={styles.profileHeader}>
        <div style={styles.profileAvatar}>{client.name.charAt(0).toUpperCase()}</div>
        {editing ? (
          <div style={{ ...styles.profileInfo, flex: 1 }}>
            {editError && <div style={styles.error}>{editError}</div>}
            <div style={styles.formGrid}>
              <Field label="Client Name *" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} required />
              <Field label="Company (optional)" value={editForm.company} onChange={v => setEditForm(f => ({ ...f, company: v }))} />
              <Field label="Phone" value={editForm.phone} onChange={v => setEditForm(f => ({ ...f, phone: v }))} type="tel" />
              <Field label="Email" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} type="email" />
              <div style={styles.field}>
                <label style={styles.label}>Client Type</label>
                <select style={styles.input} value={editForm.client_type} onChange={e => setEditForm(f => ({ ...f, client_type: e.target.value }))}>
                  {['residential', 'commercial', 'property_manager', 'builder', 'other'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.replace(/_/g, ' ').slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <Field label="Property / Service Address" value={editForm.address} onChange={v => setEditForm(f => ({ ...f, address: v }))} full />
            <Field label="Billing Address (if different)" value={editForm.billing_address} onChange={v => setEditForm(f => ({ ...f, billing_address: v }))} full />
            <Field label="Notes" value={editForm.notes} onChange={v => setEditForm(f => ({ ...f, notes: v }))} full textarea />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button style={styles.addJobBtn} onClick={saveClientEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save Changes'}</button>
              <button style={styles.toggleBtn} onClick={() => setEditing(false)} disabled={savingEdit}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={styles.profileInfo}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h1 style={styles.profileName}>{client.name}</h1>
              {!isActive && <span style={styles.inactiveBadge}>Inactive</span>}
            </div>
            {client.company && <p style={styles.company}>{client.company}</p>}
            <div style={styles.contactRow}>
              {client.phone && <span>📞 {client.phone}</span>}
              {client.email && <span>✉️ {client.email}</span>}
              {client.address && <span>📍 {client.address}</span>}
              {client.billing_address && <span>🧾 Billing: {client.billing_address}</span>}
            </div>
          </div>
        )}
        {!editing && isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            <button style={{ ...styles.toggleBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }} onClick={startEdit}>
              ✏️ Edit
            </button>
            <button
              style={{ ...styles.toggleBtn, background: isActive ? '#fef2f2' : '#f0fdf4', color: isActive ? '#dc2626' : '#16a34a', border: `1px solid ${isActive ? '#fecaca' : '#bbf7d0'}` }}
              onClick={toggleActive}
              disabled={toggling}
            >
              {toggling ? '...' : isActive ? 'Mark Inactive' : 'Mark Active'}
            </button>
            <Link to={`/jobs/new?client=${id}`} style={styles.addJobBtn}>+ New Job</Link>
            <button style={styles.addJobBtn} onClick={createSiteVisit} disabled={creatingVisit}>
              {creatingVisit ? 'Creating...' : '+ New Site Visit'}
            </button>
            <button style={styles.deleteBtn} onClick={deleteClient} disabled={deleting}>
              {deleting ? 'Deleting...' : '🗑️ Delete Client'}
            </button>
          </div>
        )}
        {!editing && !isAdmin && (
          <button style={styles.addJobBtn} onClick={createSiteVisit} disabled={creatingVisit}>
            {creatingVisit ? 'Creating...' : '+ New Site Visit'}
          </button>
        )}
      </div>

      {isAdmin && (
        <div style={{ ...styles.statsRow, ...(isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}) }}>
          <div style={styles.statBox}>
            <div style={styles.statNum}>{jobs.length}</div>
            <div style={styles.statLbl}>Total Jobs</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNum}>{jobs.filter(j => j.status === 'active').length}</div>
            <div style={styles.statLbl}>Active</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNum}>${totalRevenue.toLocaleString()}</div>
            <div style={styles.statLbl}>Total Revenue</div>
          </div>
        </div>
      )}

      {client.notes && (
        <div style={styles.notesBox}>
          <strong>Notes:</strong> {client.notes}
        </div>
      )}

      <RemindersPanel clientId={id} title="Follow-Ups" />

      <div style={{ ...styles.section, marginBottom: '16px' }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Site Visits</h2>
          <button style={{ ...styles.addJobBtn, fontSize: '12px', padding: '6px 12px' }} onClick={createSiteVisit} disabled={creatingVisit}>
            {creatingVisit ? 'Creating...' : '+ New Site Visit'}
          </button>
        </div>
        {siteVisits.length === 0 ? (
          <div style={styles.empty}>No site visits yet for this client.</div>
        ) : (
          <div style={styles.jobList}>
            {siteVisits.map(v => (
              <Link to={`/site-visits/${v.id}`} key={v.id} style={styles.jobCard}>
                <div style={styles.jobLeft}>
                  <span style={styles.jobTitle}>{v.title || 'Untitled Site Visit'}</span>
                  <span style={styles.jobDate}>{new Date(v.created_at).toLocaleDateString()}</span>
                </div>
                <span style={{ ...styles.statusBadge, background: SITE_VISIT_STATUS_COLOR[v.status] || '#6b7280' }}>{v.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Job History</h2>
          <Link to={`/jobs/new?client=${id}`} style={{ ...styles.addJobBtn, fontSize: '12px', padding: '6px 12px' }}>+ New Job</Link>
        </div>
        {jobs.length === 0 ? (
          <div style={styles.empty}>No jobs yet for this client.</div>
        ) : (
          <div style={styles.jobList}>
            {jobs.map(job => (
              <Link to={`/jobs/${job.id}`} key={job.id} style={styles.jobCard}>
                <div style={styles.jobLeft}>
                  <span style={styles.jobTitle}>{job.title}</span>
                  <span style={styles.jobDate}>{new Date(job.created_at).toLocaleDateString()}</span>
                </div>
                <div style={styles.jobRight}>
                  {isAdmin && job.contract_amount && (
                    <span style={styles.jobAmount}>${job.contract_amount.toLocaleString()}</span>
                  )}
                  <span style={{ ...styles.statusBadge, background: statusColor[job.status] || '#6b7280' }}>
                    {job.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      {isAdmin && estimates.length > 0 && (
        <div style={{ ...styles.section, marginTop: '16px' }}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Estimates</h2>
            <Link to={`/estimates/new?client=${id}`} style={{ ...styles.addJobBtn, fontSize: '12px', padding: '6px 12px' }}>+ New Estimate</Link>
          </div>
          <div style={styles.jobList}>
            {estimates.map(est => (
              <Link to={`/estimates/${est.id}/edit`} key={est.id} style={styles.jobCard}>
                <div style={styles.jobLeft}>
                  <span style={styles.jobTitle}>Quote #{est.quote_number || est.id.slice(0,6)}</span>
                  <span style={styles.jobDate}>{new Date(est.created_at).toLocaleDateString()}</span>
                </div>
                <div style={styles.jobRight}>
                  <span style={styles.jobAmount}>${(est.suggested_price || 0).toLocaleString()}</span>
                  <span style={{ ...styles.statusBadge, background: { draft:'#6b7280', sent:'#2563eb', approved:'#16a34a', declined:'#dc2626' }[est.status] || '#6b7280' }}>{est.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {isAdmin && proposals.length > 0 && (
        <div style={{ ...styles.section, marginTop: '16px' }}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Proposals</h2>
          </div>
          <div style={styles.jobList}>
            {proposals.map(prop => (
              <Link to={`/proposals/${prop.id}`} key={prop.id} style={styles.jobCard}>
                <div style={styles.jobLeft}>
                  <span style={styles.jobTitle}>{prop.proposal_number}</span>
                  <span style={styles.jobDate}>{new Date(prop.created_at).toLocaleDateString()}</span>
                </div>
                <div style={styles.jobRight}>
                  <span style={styles.jobAmount}>${((prop.selected_contract_total || 0) + (prop.tax_amount || 0)).toLocaleString()}</span>
                  <span style={{ ...styles.statusBadge, background: { draft:'#6b7280', sent:'#2563eb', viewed:'#7c3aed', approved:'#16a34a', declined:'#dc2626', expired:'#6b7280' }[prop.status] || '#6b7280' }}>{prop.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {isAdmin && invoices.length > 0 && (
        <div style={{ ...styles.section, marginTop: '16px' }}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Invoices</h2>
          </div>
          <div style={styles.jobList}>
            {invoices.map(inv => (
              <Link to={`/invoices/${inv.id}`} key={inv.id} style={styles.jobCard}>
                <div style={styles.jobLeft}>
                  <span style={styles.jobTitle}>{inv.invoice_number || `INV-${inv.id.slice(0,6)}`}</span>
                  {inv.due_date && <span style={styles.jobDate}>Due {new Date(inv.due_date).toLocaleDateString()}</span>}
                </div>
                <div style={styles.jobRight}>
                  <span style={styles.jobAmount}>${getInvoiceTotal(inv).toLocaleString()}</span>
                  <span style={{ ...styles.statusBadge, background: { draft:'#6b7280', sent:'#2563eb', paid:'#16a34a', overdue:'#dc2626' }[inv.status] || '#6b7280' }}>{inv.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function NewClient() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', address: '', billing_address: '', notes: '', client_type: 'residential' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    const { data, error } = await supabase.from('clients').insert([{ ...form, active: true }]).select().single()
    if (error) { setError(error.message); setSaving(false); return }
    navigate(`/clients/${data.id}`)
  }

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.back}>← Back</button>
      <h1 style={styles.title}>Add New Client</h1>
      <form onSubmit={handleSubmit} style={styles.form}>
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.formGrid}>
          <Field label="Client Name *" value={form.name} onChange={v => set('name', v)} required />
          <Field label="Company (optional)" value={form.company} onChange={v => set('company', v)} />
          <Field label="Phone" value={form.phone} onChange={v => set('phone', v)} type="tel" />
          <Field label="Email" value={form.email} onChange={v => set('email', v)} type="email" />
          <div style={styles.field}>
            <label style={styles.label}>Client Type</label>
            <select style={styles.input} value={form.client_type} onChange={e => set('client_type', e.target.value)}>
              {['residential', 'commercial', 'property_manager', 'builder', 'other'].map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.replace(/_/g, ' ').slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
        <Field label="Property Address" value={form.address} onChange={v => set('address', v)} full />
        <Field label="Billing Address (if different)" value={form.billing_address} onChange={v => set('billing_address', v)} full />
        <Field label="Notes" value={form.notes} onChange={v => set('notes', v)} full textarea />
        <button style={styles.submitBtn} type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Client'}
        </button>
      </form>
    </div>
  )
}

function Field({ label, value, onChange, required, type = 'text', full, textarea }) {
  return (
    <div style={{ ...styles.field, ...(full ? styles.fieldFull : {}) }}>
      <label style={styles.label}>{label}</label>
      {textarea ? (
        <textarea style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} value={value} onChange={e => onChange(e.target.value)} />
      ) : (
        <input style={styles.input} type={type} value={value} onChange={e => onChange(e.target.value)} required={required} />
      )}
    </div>
  )
}

const styles = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  addBtn: { background: '#2d4a2d', color: 'white', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },
  search: { width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', marginBottom: '12px', boxSizing: 'border-box', fontFamily: 'inherit' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px' },
  filterBtn: { padding: '6px 14px', borderRadius: '20px', border: '1.5px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' },
  filterActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'white', borderRadius: '10px', textDecoration: 'none', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  avatar: { width: '44px', height: '44px', borderRadius: '50%', background: '#2d4a2d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', flexShrink: 0 },
  info: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  name: { fontWeight: '600', color: '#1a2e1a', fontSize: '16px' },
  detail: { fontSize: '13px', color: '#6b7c6b' },
  inactiveBadge: { background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  jobCount: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  jobNum: { fontSize: '20px', fontWeight: '700', color: '#2d4a2d' },
  jobLabel: { fontSize: '11px', color: '#6b7c6b', textTransform: 'uppercase' },
  empty: { textAlign: 'center', padding: '40px 0', color: '#6b7c6b' },
  back: { background: 'none', border: 'none', color: '#2d4a2d', cursor: 'pointer', fontSize: '14px', fontWeight: '600', padding: '0 0 16px', display: 'block' },
  profileHeader: { display: 'flex', gap: '20px', alignItems: 'flex-start', background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  profileAvatar: { width: '60px', height: '60px', borderRadius: '50%', background: '#2d4a2d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '700', flexShrink: 0 },
  profileInfo: { flex: 1 },
  profileName: { margin: '0 0 4px', fontSize: '24px', fontWeight: '700', color: '#1a2e1a' },
  company: { margin: '0 0 8px', color: '#6b7c6b', fontSize: '15px' },
  contactRow: { display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '14px', color: '#374151' },
  toggleBtn: { padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  addJobBtn: { background: '#2d4a2d', color: 'white', padding: '8px 16px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' },
  deleteBtn: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' },
  statBox: { background: 'white', borderRadius: '10px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  statNum: { fontSize: '24px', fontWeight: '700', color: '#1a2e1a' },
  statLbl: { fontSize: '12px', color: '#6b7c6b', textTransform: 'uppercase', marginTop: '4px' },
  notesBox: { background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#374151' },
  section: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  sectionTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  jobList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  jobCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#f9fafb', borderRadius: '8px', textDecoration: 'none', border: '1px solid #e5e7eb' },
  jobLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  jobTitle: { fontWeight: '600', color: '#1a2e1a', fontSize: '15px' },
  jobDate: { fontSize: '12px', color: '#6b7c6b' },
  jobRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  jobAmount: { fontWeight: '600', color: '#2d4a2d', fontSize: '15px' },
  statusBadge: { color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' },
  form: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px' },
  field: {},
  fieldFull: { gridColumn: '1 / -1' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box' },
  submitBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '14px 28px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' }
}
